import { lots } from "@/api/lots/useLotsQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { BarcodeSlittingForm } from "@/features/outSourcingSlitting/form/barcodeSlitiingForm";
import customAlert from "@/features/ui/alert/alert";
import useWorkMutation from "@/features/workByManagement/api/useWorkMutation";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useWorkState } from "@/features/workByManagement/hooks/useWorkState";
import { WorkUpdateForm } from "@/features/workByManagement/WorkUpdateForm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button } from "@mantine/core";
import {
  ProductionPlansGet200ResponseRowsInnerWorksInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
interface WorkSlittingRightActionButtonBarProps {
  matchedData: Record<number, WorksSlittingScrapDetailPost200ResponseInner>;
  refetch: () => void;
  setUpdateSuccess: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  partChecked?: Record<number, boolean>;
}

export const WorkSlittingRightActionButtonBar = (params: WorkSlittingRightActionButtonBarProps) => {
  const { matchedData, refetch, setUpdateSuccess, setLoading, partChecked } = params;
  const { t } = useTranslation();
  const state = useWorkState();
  const actions = useWorkActions();
  const { openModal } = useModal();
  const { deleteMutate, workSlittingMutation } = useWorkMutation();

  const lotName = Object.values(matchedData)?.find(
    (data) =>
      state.scrapWorkIds &&
      data.workId !== undefined &&
      Object.values(state.scrapWorkIds).includes(data.workId), // includes로 수정
  )?.materialLotName;

  const { data } = useQuery({
    ...lots.get({
      query: {
        $and: [
          {
            name: {
              $eq: lotName,
            },
          },
        ],
      },
    }),
    enabled: Boolean(lotName),
  });
  const lotId = data?.data?.rows && data?.data?.rows[0]?.id;
  const handleUpdateQuantities = () => {
    const workRequests = state.works
      .flatMap((work) => {
        const matched = Object.values(matchedData)?.find((data) => data.workId === work.id);
        if (!matched) return null;
        const isChecked = work.id !== undefined && Boolean(partChecked?.[work.id] ?? false);
        const { usedMaterialWeight, scrapWeight, materialWeightUnit, productionWidth } = matched;

        if (work.id && usedMaterialWeight && isChecked) {
          const halfConsume = Number(usedMaterialWeight) / 2;
          const halfScrap = Number(scrapWeight) / 2;
          return [
            {
              workId: work.id,
              quantity: "0.5",
              consumeQuantity: halfConsume.toString(),
              scrapWeight: halfScrap.toString(),
              scrapWeightUnit: materialWeightUnit,
              productionWidth,
              consumeLotId: lotId,
              splitLot: true,
              priorityConsume: true, // 투입한거 우선소모 boolean
            },
            {
              workId: work.id,
              quantity: "0.5",
              consumeQuantity: halfConsume.toString(),
              scrapWeight: halfScrap.toString(),
              scrapWeightUnit: materialWeightUnit,
              productionWidth,
              consumeLotId: lotId,
              splitLot: true,
              priorityConsume: true, // 투입한거 우선소모 boolean
            },
          ];
        } else if (work.id && usedMaterialWeight) {
          return {
            workId: work.id,
            quantity: "1",
            consumeQuantity: usedMaterialWeight,
            scrapWeight,
            scrapWeightUnit: materialWeightUnit,
            productionWidth,
            consumeLotId: lotId,
            splitLot: false,
            priorityConsume: true, // 투입한거 우선소모 boolean
          };
        }

        return null;
      })
      .filter(Boolean);

    if (workRequests.length > 0) {
      try {
        setLoading(true);

        workSlittingMutation(
          {
            worksGroupSlittingPutRequest: {
              workRequest: workRequests.filter(
                (
                  request,
                ): request is {
                  workId: number;
                  quantity: string;
                  consumeQuantity: string;
                  scrapWeight: string;
                  scrapWeightUnit: string;
                  productionWidth: string;
                  consumeLotId: number;
                  splitLot: boolean;
                  priorityConsume: boolean;
                } => request !== null,
              ), // 모든 workId와 quantity를 포함한 배열
            },
          },
          {
            onSuccess: (res) => {
              const data = res.data;
              actions.setTempQuantity([]);
              setUpdateSuccess(true);
              refetch(); // 데이터 다시 불러오기
              actions.setWorks((prevWorks) => []);
              setLoading(false);
              openModal(<BarcodeSlittingForm data={data} />, null, t("barcode.printer"), true);
            },
            onError: (error) => {
              setLoading(false);
              customAlert(
                handleErrorResponse({ error, errorContext: t("workButton.save") }),
                t("workMessage.slittingFail"),
                "red",
              );
            },
          },
        );
      } catch (error) {
        alert(t("workMessage.updatedFail"));
      }
    } else {
      alert(t("workMessage.noValidData"));
    }
  };

  return (
    <>
      <Button
        color="yellow"
        disabled={!(state.works && state.works.length === 1)}
        onClick={() => {
          const works = state.works;
          openModal(
            <WorkUpdateForm<"plan">
              workRowData={works as ProductionPlansGet200ResponseRowsInnerWorksInner[]}
            />,
            null,
            `${t("work.work")} ${t("workButton.updated")}`,
            true,
          ).then((result) => {
            if (result === true) {
              actions.setWorks((prevWorks) => []);
              actions.setTempDefects([]);
              setUpdateSuccess(true);
              refetch();
            }
          });
        }}
      >
        {t("workButton.updated")}
      </Button>
      <Button
        color="red"
        disabled={!(state.works && state.works.length >= 1)}
        onClick={() => {
          if (window.confirm(t("workMessage.delete"))) {
            state.works.forEach((work) => {
              work.id && deleteMutate({ workId: work.id });
            });
          }
        }}
      >
        {t("workButton.delete")}
      </Button>
      <Button
        disabled={
          !(
            state.scrapWorkIds &&
            Object.keys(state.scrapWorkIds).length > 0 &&
            Object.values(state.scrapWorkIds).every((id) => matchedData[id]) &&
            Object.values(matchedData)
              .filter((data) => state.scrapWorkIds && state.scrapWorkIds[data.workId ?? 0])
              .some((data) => data.scrapWeight) &&
            state.works.some((data) => data.trackingStatus !== "DONE")
          )
        }
        onClick={handleUpdateQuantities}
      >
        {t("workButton.save")}
      </Button>
    </>
  );
};
